import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Phone, ShieldCheck } from "lucide-react";

/**
 * PhoneOtpAuth — primary auth component.
 * - Step 1: enter phone, request OTP (MSG91 Widget API).
 * - Step 2: enter 6-digit OTP + (optional) name, verify to sign in / sign up.
 */
export default function PhoneOtpAuth({ mode = "login", onSuccess, redirectTo = "/dashboard" }) {
  const { requestPhoneOtp, verifyPhoneOtp } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("+91 ");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [reqId, setReqId] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Enter your phone number");
      return;
    }
    setLoading(true);
    const r = await requestPhoneOtp(phone.trim());
    setLoading(false);
    if (!r.ok) {
      toast.error(r.error);
      return;
    }
    setReqId(r.reqId || "");
    toast.success(`OTP sent to ${r.phone}`);
    setStep("otp");
  };

  const resend = async () => {
    setLoading(true);
    const r = await requestPhoneOtp(phone.trim());
    setLoading(false);
    if (!r.ok) {
      toast.error(r.error);
      return;
    }
    setReqId(r.reqId || "");
    toast.success("OTP resent");
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!otp.trim()) {
      toast.error("Enter the OTP");
      return;
    }
    setLoading(true);
    const r = await verifyPhoneOtp(phone.trim(), otp.trim(), name.trim(), reqId);
    setLoading(false);
    if (!r.ok) {
      toast.error(r.error);
      return;
    }
    toast.success(mode === "register" ? "Account ready!" : "Welcome back!");
    if (onSuccess) onSuccess(r.user);
    else nav(redirectTo);
  };

  if (step === "phone") {
    return (
      <form onSubmit={requestOtp} className="space-y-4" data-testid="phone-otp-step-phone">
        <div>
          <Label htmlFor="phone-otp-phone" className="font-semibold">Phone number</Label>
          <div className="relative mt-1">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="phone-otp-phone"
              data-testid="phone-otp-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className="pl-9 border border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500 h-11"
              inputMode="tel"
              autoComplete="tel"
              required
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">We'll text you a 6-digit code. Indian numbers default to +91.</p>
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="phone-otp-send-btn"
          className="w-full bg-brand hover:bg-brand-dark text-white rounded-full shadow-btn font-bold h-11"
        >
          {loading ? "Sending OTP…" : "Send OTP"}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-4" data-testid="phone-otp-step-otp">
      <div className="text-sm text-slate-600">
        Code sent to <span className="font-semibold text-slate-900">{phone}</span>
        <button type="button" onClick={() => setStep("phone")} className="ml-2 text-blue-700 font-semibold underline" data-testid="phone-otp-change-number-btn">change</button>
      </div>
      <div>
        <Label htmlFor="phone-otp-code" className="font-semibold">6-digit OTP</Label>
        <div className="relative mt-1">
          <ShieldCheck className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            id="phone-otp-code"
            data-testid="phone-otp-code-input"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            className="pl-9 tracking-[0.4em] font-mono border border-slate-200 h-11"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
        </div>
      </div>
      {mode === "register" && (
        <div>
          <Label htmlFor="phone-otp-name" className="font-semibold">Your name <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input
            id="phone-otp-name"
            data-testid="phone-otp-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Priya Sharma"
            className="mt-1 border border-slate-200 h-11"
          />
        </div>
      )}
      <Button
        type="submit"
        disabled={loading}
        data-testid="phone-otp-verify-btn"
        className="w-full bg-brand hover:bg-brand-dark text-white rounded-full shadow-btn font-bold h-11"
      >
        {loading ? "Verifying…" : mode === "register" ? "Verify & create account" : "Verify & sign in"}
      </Button>
      <button
        type="button"
        onClick={resend}
        disabled={loading}
        className="w-full text-sm font-semibold text-blue-700 underline disabled:opacity-50"
        data-testid="phone-otp-resend-btn"
      >
        Resend OTP
      </button>
    </form>
  );
}
